import { PHARMACY_NPIS } from '../../pharmacies';
import type { MedicationProduct } from '../../shared';

import {
    MEDICARE_BRIDGE_PRICE_NOTE,
    SELF_PAY_JOURNEY_PRICE_NOTE,
    SELF_PAY_PRICE_NOTE,
} from './shared';

const disclosures: MedicationProduct['disclosures'] = {
    disclaimers: [
        {
            headlinePriceTypes: ['MedicareBridge'],
            slots: ['price-card'],
            content: [
                {
                    key: 'eligibility',
                    text: '<caption><sup>*</sup>Eligibility based on <a href="https://www.cms.gov/medicare/coverage/prescription-drug-coverage/medicare-glp-1-bridge">Medicare GLP\u20111 Bridge Clinical Criteria</a>. Certain plan types excluded. Not a guarantee of coverage.</caption>',
                },
            ],
        },
    ],
};

const makeSelfPayDisclaimer = (isJourney: boolean) => {
    return `<caption><sup>*</sup>Additional taxes and fees may apply. You will be asked to certify conditions of purchase, including that you have been prescribed the Zepbound® KwikPen® for an approved use consistent with FDA-approved product labeling.<br /><a href="https://www.lilly.com/lillydirect/medicines/zepbound/zepbound-tirzepatide-full-terms-conditions#${isJourney ? 'journey' : 'selfpaysavings'}">See terms and conditions</a></caption>`;
};

const makeSelfPayPriceCard = (
    price: number,
    savings?: number
) => `<stack gap="xxxs">
    <subtitle2>Self-pay price:</subtitle2>
    <subtitle1>Starting at \$${price}/month<sup>*</sup></subtitle1>
    ${
        savings
            ? `<body2>Save up to \$${savings} per month off list price with the Zepbound® Self-Pay Journey Program if eligible.</body2>`
            : '<body2>Price subject to change.</body2>'
    }
</stack>`;

const makeBridgePriceCard = (
    price: number,
    savings?: number
) => `<flex justify="space-between">
    <subtitle2>Medicare GLP-1 Bridge</subtitle2>
    <subtitle2 data-testid="pharmacy-pricing-card-bridge-price">$50.00/month<sup>*</sup></subtitle2>
</flex>
<hr color="primary" />
<stack gap="xxxs">
    <flex justify="space-between">
        <subtitle2>Self-pay</subtitle2>
        <subtitle2 data-testid="pharmacy-pricing-card-self-pay-price">Starting at \$${price}/month<sup>‡</sup></subtitle2>
    </flex>
    ${savings ? `<body2>Save up to \$${savings} per month off list price with the Zepbound® Self-Pay Journey Program if eligible.</body2>` : ''}
</stack>`;

export const zepboundKwikpens: MedicationProduct[] = [
    {
        description: 'Zepbound® 2.5 mg/0.6 mL Multi-Dose KwikPen®',
        dosageForm: 'SOPN',
        formLabel: 'Solution Pen-injector',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 2.5,
        imageUrl: '/static/img/zepbound-kwikpen-2.5mg.png',
        imageOrientation: 'landscape',
        ndcs: [
            { ndc: '00002356601', packageQuantity: 1 },
            { ndc: '00002356611', packageQuantity: 1 },
            { ndc: '00002356699', packageQuantity: 1 },
        ],
        pricing: { selfPay: 299, regularPrice: 299 },
        headlinePrices: [
            {
                type: 'Cash',
                value: 299,
                content: {
                    pricingCard: [
                        { key: 'price', text: makeSelfPayPriceCard(299) },
                    ],
                    priceNote: [
                        {
                            key: 'disclaimer',
                            text: makeSelfPayDisclaimer(false),
                        },
                    ],
                },
            },
            {
                type: 'Incentive',
                value: 299,
                qualifier: { text: '/month' },
                content: {
                    pricingCard: [
                        { key: 'price', text: makeSelfPayPriceCard(299) },
                    ],
                    priceNote: [
                        {
                            key: 'disclaimer',
                            /*
                             * This is wrong. It should be `true`, but that
                             * would show the journey link, and we need to show
                             * the NON-journey link, because this NDC doesn't
                             * technically have a journey program even though it
                             * supposedly does in the code. It's bad. Don't
                             * think about it too hard. I'm sorry.
                             */
                            text: makeSelfPayDisclaimer(false),
                        },
                    ],
                },
            },
            {
                type: 'MedicareBridge',
                value: 50,
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
                content: {
                    pricingCard: [
                        {
                            key: 'bridge-price',
                            text: makeBridgePriceCard(299),
                        },
                    ],
                    priceNote: [
                        { key: 'bridge', text: MEDICARE_BRIDGE_PRICE_NOTE },
                        { key: 'self-pay', text: SELF_PAY_PRICE_NOTE },
                    ],
                },
            },
            {
                type: 'GovernmentPriceAtPharmacy',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.AMAZON],
            },
        ],
        disclosures,
    },
    {
        description: 'Zepbound® 5 mg/0.6 mL Multi-Dose KwikPen®',
        dosageForm: 'SOPN',
        formLabel: 'Solution Pen-injector',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 5,
        imageUrl: '/static/img/zepbound-kwikpen-5mg.png',
        imageOrientation: 'landscape',
        ndcs: [
            { ndc: '00002355501', packageQuantity: 1 },
            { ndc: '00002355511', packageQuantity: 1 },
        ],
        pricing: { selfPay: 399, regularPrice: 399 },
        headlinePrices: [
            {
                type: 'Cash',
                value: 399,
                content: {
                    pricingCard: [
                        { key: 'price', text: makeSelfPayPriceCard(399) },
                    ],
                    priceNote: [
                        {
                            key: 'disclaimer',
                            text: makeSelfPayDisclaimer(false),
                        },
                    ],
                },
            },
            {
                type: 'Incentive',
                value: 399,
                qualifier: { text: '/month' },
                content: {
                    pricingCard: [
                        { key: 'price', text: makeSelfPayPriceCard(399) },
                    ],
                    priceNote: [
                        {
                            key: 'disclaimer',
                            /*
                             * This is wrong. It should be `true`, but that
                             * would show the journey link, and we need to show
                             * the NON-journey link, because this NDC doesn't
                             * technically have a journey program even though it
                             * supposedly does in the code. It's bad. Don't
                             * think about it too hard. I'm sorry.
                             */
                            text: makeSelfPayDisclaimer(false),
                        },
                    ],
                },
            },
            {
                type: 'MedicareBridge',
                value: 50,
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
                content: {
                    pricingCard: [
                        {
                            key: 'bridge-price',
                            text: makeBridgePriceCard(399),
                        },
                    ],
                    priceNote: [
                        { key: 'bridge', text: MEDICARE_BRIDGE_PRICE_NOTE },
                        { key: 'self-pay', text: SELF_PAY_PRICE_NOTE },
                    ],
                },
            },
            {
                type: 'GovernmentPriceAtPharmacy',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.AMAZON],
            },
        ],
        disclosures,
    },
    {
        description: 'Zepbound® 7.5 mg/0.6 mL Multi-Dose KwikPen®',
        dosageForm: 'SOPN',
        formLabel: 'Solution Pen-injector',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 7.5,
        imageUrl: '/static/img/zepbound-kwikpen-7.5mg.png',
        imageOrientation: 'landscape',
        ndcs: [
            { ndc: '00002354401', packageQuantity: 1 },
            { ndc: '00002354411', packageQuantity: 1 },
        ],
        pricing: { selfPay: 449, regularPrice: 499 },
        headlinePrices: [
            {
                type: 'Cash',
                value: 499,
                content: {
                    pricingCard: [
                        { key: 'price', text: makeSelfPayPriceCard(499) },
                    ],
                    priceNote: [
                        {
                            key: 'disclaimer',
                            text: makeSelfPayDisclaimer(false),
                        },
                    ],
                },
            },
            {
                type: 'Incentive',
                value: 449,
                qualifier: { text: '/month' },
                content: {
                    pricingCard: [
                        { key: 'price', text: makeSelfPayPriceCard(449, 50) },
                    ],
                    priceNote: [
                        {
                            key: 'disclaimer',
                            text: makeSelfPayDisclaimer(true),
                        },
                    ],
                },
            },
            {
                type: 'MedicareBridge',
                value: 50,
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
                content: {
                    pricingCard: [
                        {
                            key: 'bridge-price',
                            text: makeBridgePriceCard(449, 50),
                        },
                    ],
                    priceNote: [
                        { key: 'bridge', text: MEDICARE_BRIDGE_PRICE_NOTE },
                        { key: 'self-pay', text: SELF_PAY_JOURNEY_PRICE_NOTE },
                    ],
                },
            },
            {
                type: 'GovernmentPriceAtPharmacy',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.AMAZON],
            },
        ],
        disclosures,
    },
    {
        description: 'Zepbound® 10 mg/0.6 mL Multi-Dose KwikPen®',
        dosageForm: 'SOPN',
        formLabel: 'Solution Pen-injector',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 10,
        imageUrl: '/static/img/zepbound-kwikpen-10mg.png',
        imageOrientation: 'landscape',
        ndcs: [
            { ndc: '00002353301', packageQuantity: 1 },
            { ndc: '00002353311', packageQuantity: 1 },
        ],
        pricing: { selfPay: 449, regularPrice: 699 },
        headlinePrices: [
            {
                type: 'Cash',
                value: 699,
                content: {
                    pricingCard: [
                        { key: 'price', text: makeSelfPayPriceCard(699) },
                    ],
                    priceNote: [
                        {
                            key: 'disclaimer',
                            text: makeSelfPayDisclaimer(false),
                        },
                    ],
                },
            },
            {
                type: 'Incentive',
                value: 449,
                qualifier: { text: '/month' },
                content: {
                    pricingCard: [
                        { key: 'price', text: makeSelfPayPriceCard(449, 250) },
                    ],
                    priceNote: [
                        {
                            key: 'disclaimer',
                            text: makeSelfPayDisclaimer(true),
                        },
                    ],
                },
            },
            {
                type: 'MedicareBridge',
                value: 50,
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
                content: {
                    pricingCard: [
                        {
                            key: 'bridge-price',
                            text: makeBridgePriceCard(449, 250),
                        },
                    ],
                    priceNote: [
                        { key: 'bridge', text: MEDICARE_BRIDGE_PRICE_NOTE },
                        { key: 'self-pay', text: SELF_PAY_JOURNEY_PRICE_NOTE },
                    ],
                },
            },
            {
                type: 'GovernmentPriceAtPharmacy',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.AMAZON],
            },
        ],
        disclosures,
    },
    {
        description: 'Zepbound® 12.5 mg/0.6 mL Multi-Dose KwikPen®',
        dosageForm: 'SOPN',
        formLabel: 'Solution Pen-injector',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 12.5,
        imageUrl: '/static/img/zepbound-kwikpen-12.5mg.png',
        imageOrientation: 'landscape',
        ndcs: [
            { ndc: '00002352201', packageQuantity: 1 },
            { ndc: '00002352211', packageQuantity: 1 },
        ],
        pricing: { selfPay: 449, regularPrice: 699 },
        headlinePrices: [
            {
                type: 'Cash',
                value: 699,
                content: {
                    pricingCard: [
                        { key: 'price', text: makeSelfPayPriceCard(699) },
                    ],
                    priceNote: [
                        {
                            key: 'disclaimer',
                            text: makeSelfPayDisclaimer(false),
                        },
                    ],
                },
            },
            {
                type: 'Incentive',
                value: 449,
                qualifier: { text: '/month' },
                content: {
                    pricingCard: [
                        { key: 'price', text: makeSelfPayPriceCard(449, 250) },
                    ],
                    priceNote: [
                        {
                            key: 'disclaimer',
                            text: makeSelfPayDisclaimer(true),
                        },
                    ],
                },
            },
            {
                type: 'MedicareBridge',
                value: 50,
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
                content: {
                    pricingCard: [
                        {
                            key: 'bridge-price',
                            text: makeBridgePriceCard(449, 250),
                        },
                    ],
                    priceNote: [
                        { key: 'bridge', text: MEDICARE_BRIDGE_PRICE_NOTE },
                        { key: 'self-pay', text: SELF_PAY_JOURNEY_PRICE_NOTE },
                    ],
                },
            },
            {
                type: 'GovernmentPriceAtPharmacy',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.AMAZON],
            },
        ],
        disclosures,
    },
    {
        description: 'Zepbound® 15 mg/0.6 mL Multi-Dose KwikPen®',
        dosageForm: 'SOPN',
        formLabel: 'Solution Pen-injector',
        packageSizeUnit: 'EA',
        strengthUnit: 'mg',
        strengthValue: 15,
        imageUrl: '/static/img/zepbound-kwikpen-15mg.png',
        imageOrientation: 'landscape',
        ndcs: [
            { ndc: '00002351101', packageQuantity: 1 },
            { ndc: '00002351111', packageQuantity: 1 },
        ],
        pricing: { selfPay: 449, regularPrice: 699 },
        headlinePrices: [
            {
                type: 'Cash',
                value: 699,
                content: {
                    pricingCard: [
                        { key: 'price', text: makeSelfPayPriceCard(699) },
                    ],
                    priceNote: [
                        {
                            key: 'disclaimer',
                            text: makeSelfPayDisclaimer(false),
                        },
                    ],
                },
            },
            {
                type: 'Incentive',
                value: 449,
                qualifier: { text: '/month' },
                content: {
                    pricingCard: [
                        { key: 'price', text: makeSelfPayPriceCard(449, 250) },
                    ],
                    priceNote: [
                        {
                            key: 'disclaimer',
                            text: makeSelfPayDisclaimer(true),
                        },
                    ],
                },
            },
            {
                type: 'MedicareBridge',
                value: 50,
                pharmacyNpis: [PHARMACY_NPIS.GIFTHEALTH],
                content: {
                    pricingCard: [
                        {
                            key: 'bridge-price',
                            text: makeBridgePriceCard(449, 250),
                        },
                    ],
                    priceNote: [
                        { key: 'bridge', text: MEDICARE_BRIDGE_PRICE_NOTE },
                        { key: 'self-pay', text: SELF_PAY_JOURNEY_PRICE_NOTE },
                    ],
                },
            },
            {
                type: 'GovernmentPriceAtPharmacy',
                value: -1,
                pharmacyNpis: [PHARMACY_NPIS.AMAZON],
            },
        ],
        disclosures,
    },
];
